import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from 'lodash';
import { DataGridWrapper } from '../../../../Utils/DataGridWrapper/DataGridWrapper';
import { getUserProgressReportAPI, getOrganizationProgressStatsAPI, exportUserProgressReportAsExcelAPI } from '../../../../Services/api';
import { I18nReactContext } from '../../../../contexts/I18nContext';
import { UserDetailsCell } from '../../../Settings/DeletedUsers/UserDetailsCell/UserDetailsCell';
import { PieChart } from '../../../Charts/PieChart/PieChart';
import { Colors } from '../../../../Utils/styles/Colors';
import { getSignDocOptions } from '../../SignDocsStats/SignDocsStatsLib';
import { ProgressBar } from '../../../ProgressBar/ProgressBar';
import { LocalPopover } from '../../../../Utils/Popover/Popover';
import { GridReactContext } from '../../../../Utils/DataGridWrapper/GridContext';
import { PhoneCell } from '../../../../Utils/PhoneCell/PhoneCell';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import './UserProgressReportTable.scss';
import {UserReactContext} from '../../../../contexts/UserContext';
import {SignDocCategories} from '../../../../Utils/Constants';



/**
 * Gets the column configuration for the user progress report table
 * 
 * @param {object} params - The function parameters container object
 * @param {object} params.i18n - Translation context object
 * @returns {Array} Array of column configurations for the data grid
 */
const getProgressReportColumns = ({ i18n, isCompanyAdmin }) => {
    return [
        {
            field: 'firstName',
            headerName: i18n.translate("userProgressReport.columns.fullName"),
            minWidth: 160,
            align: 'right',
            // Sorts by first name then last name - see PROGRESS_REPORT_SORT_COLUMNS on the server
            sortable: true,
            renderCell: (params) => <div style={{width: '100%'}}>{`${params.value} ${params.row.lastName}`}</div>,
        },
        {
            field: 'phoneFormatted',
            headerName: i18n.translate('userProgressReport.columns.phone'),
            minWidth: 150,
            align: 'right',
            // Not sortable: ordering people by phone number has no meaning for this report
            sortable: false,
            renderCell: (params) => <PhoneCell value={params.value} />,
        },
        {
            field: 'organizations',
            headerName: i18n.translate('userProgressReport.columns.organizations'),
            minWidth: 250,
            align: 'right',
            sortable: false,
            renderCell: (params) => {
                const organizations = get(params.row, 'organizations', []);
                return organizations.length === 0 ? <div style={{ width: '100%', color: '#999' }}>-</div> 
                : (
                    <div className="organizationsWrapper">
                        {organizations.map((org, index) => (
                            <span key={index} className="organizationName">
                                {org.orgName}
                            </span>
                        ))}
                    </div>
                );
            },
        },
        {
            field: 'idNumber',
            headerName: i18n.translate('userProgressReport.columns.idNumber'),
            minWidth: 140,
            align: 'right',
            sortable: true,
            renderCell: (params) => <div style={{ width: '100%' }} title={params.value}>{params.value || '-'}</div>,
        },
        {
            field: 'employeeId',
            headerName: i18n.translate('userProgressReport.columns.employeeId'),
            minWidth: 140,
            align: 'right',
            sortable: true,
            renderCell: (params) => <div style={{ width: '100%' }} title={params.value}>{params.value || '-'}</div>,
        },
        {
            field: 'progressPercentage',
            headerName: i18n.translate('userProgressReport.columns.chosenOrganizationUnitsProgress'),
            minWidth: 240,
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                if (params.row.isCompanyAdmin && !isCompanyAdmin) {
                    return <div style={{ width: '100%' }} title={params.value}>-</div>;
                }
                const percentage = params.value || 0;
                const completedAssignments = get(params.row, 'completedAssignments', 0);
                const totalAssignments = get(params.row, 'totalAssignments', 0);
                
                return (
                    <div className="progressContainer">
                        <ProgressBar 
                            percentage={percentage} 
                            size="medium" 
                            className="ProgressBar--table-cell"
                            containerStyle={{ width: '70%', justifyContent: 'flex-start' }}
                            statusText={`${completedAssignments}/${totalAssignments} ${i18n.translate('userProgressReport.tasksCompleted')}`}
                        />
                    </div>
                );
            },
        },
        {
            field: 'allManagedProgressPercentage',
            headerName: i18n.translate('userProgressReport.columns.allManagedProgress'),
            minWidth: 240,
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                if (params.row.isCompanyAdmin && !isCompanyAdmin) {
                    return <div style={{ width: '100%' }} title={params.value}>-</div>;
                }
                const percentage = params.value || 0;
                const completedAssignments = get(params.row, 'allManagedCompletedAssignments', 0);
                const totalAssignments = get(params.row, 'allManagedTotalAssignments', 0);
                
                return (
                    <div className="progressContainer">
                        <ProgressBar 
                            percentage={percentage} 
                            size="medium" 
                            className="ProgressBar--table-cell"
                            containerStyle={{ width: '70%', justifyContent: 'flex-start' }}
                            statusText={`${completedAssignments}/${totalAssignments} ${i18n.translate('userProgressReport.tasksCompleted')}`}
                        />
                    </div>
                );
            },
        },
        {
            field: 'userDetails',
            headerName: i18n.translate('userProgressReport.columns.userDetails'),
            minWidth: 130,
            align: 'right',
            sortable: false,
            renderCell: (params) => {
                if (params.row.isCompanyAdmin && !isCompanyAdmin) {
                    return <div style={{ width: '100%' }} title={params.value}>-</div>;
                }
                return <UserDetailsCell user={params.row}/>;
            },
        }
    ];
};

/**
 * Converts user data to the format expected by the data grid
 * Note: DataGridWrapper passes the users array directly, not the full API response
 * 
 * @param {Array} users - Array of user objects from the API
 * @returns {Array} Array of user data formatted for the data grid
 */
const convertData = (users) => {
    if (!Array.isArray(users)) {
        return [];
    }
    
    // Ensure each user has the required fields for DataGrid and UserDetailsCell
    return users.map((user, index) => {
        return {
            id: get(user, 'id', `user-${index}`),
            ...user
        };
    });
};

/**
 * Prepares pie chart data for organization progress visualization
 * 
 * @param {object} params - Function parameters
 * @param {object} params.stats - Aggregated progress statistics
 * @param {object} params.i18n - Translation context object
 * @returns {Array} Array of pie chart data objects
 */
const preparePieChartData = ({ stats, i18n }) => {
    const { totalTasks, completedTasks, inProgressTasks, notStartedTasks } = stats;
    
    if (totalTasks === 0) {
        return [];
    }

    const data = [
        {
            label: i18n.translate('userProgressReport.pieChart.completed'),
            value: completedTasks,
            color: Colors["$GraphBackground1"] // Light green - same as home page
        },
        {
            label: i18n.translate('userProgressReport.pieChart.inProgress'),
            value: inProgressTasks,
            color: Colors["$GraphBackground2"] // Yellow - same as home page
        },
        {
            label: i18n.translate('userProgressReport.pieChart.notStarted'),
            value: notStartedTasks,
            color: Colors["$GraphBackground3"] // Light grey - same as home page
        },
    ].filter(item => item.value > 0); // Only show categories with values

    return data;
};

/**
 * Default pagination model for the data grid
 */
const paginationModel = {
    page: 0,
    pageSize: 50,
};

/**
 * Default table filters configuration
 */
const tableFilters = {
    showFilters: {
        search: true,
        organizations: true,
        organizationIntersection: true,
        signDocs: true
    },
    filterValues: {},
    filterData: {}
};

/**
 * UserProgressReportTable Component
 *
 * Displays a table of users with their progress percentages based on completed assignments
 * from organizations the admin manages. Supports pagination, filtering, and sorting.
 *
 * @param {object} params - Component parameters
 * @param {Array} params.organizationsOptions - Array of organization options for filtering
 * @param {Array} params.adminOrganizations - Full organization objects with signDoc data
 * @returns {JSX.Element} Rendered user progress report table component
 */
export const UserProgressReportTable = ({ organizationsOptions, adminOrganizations }) => {
    const { i18n } = useContext(I18nReactContext);
    const userContext = useContext(UserReactContext);
    const gridContext = useContext(GridReactContext);
    const navigate = useNavigate();
    const [pieChartData, setPieChartData] = useState([]);
    const [isExporting, setIsExporting] = useState(false);

    /**
     * Handles exporting user progress report to Excel
     */
    const handleExportToExcel = async () => {
        try {
            setIsExporting(true);
            const result = await exportUserProgressReportAsExcelAPI({
                filterModel: gridContext.filterModel
            });

            if (result.status === 'completed') {
                navigate('/reports/generatedReports');
            } else {
                console.error('Export failed:', result.error);
            }
        } catch (error) {
            console.error('Error exporting user progress report:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const tableButtons = [{
        text: i18n.translate('userProgressReport.exportToExcel'),
        icon: <CloudDownloadIcon />,
        onClick: handleExportToExcel,
        disabled: isExporting,
        loading: isExporting,
        color: 'yellow'
    }];
    
    // Initialize complete filter state with both organizations and signDocs
    const initializeFilterModel = () => {
        const validOrganizationsOptions = Array.isArray(organizationsOptions) ? organizationsOptions : [];

        // If admin has more than 20 organizations, default to first organization only
        // Otherwise, default to 'all' organizations
        const organizationCount = validOrganizationsOptions.length -1;

        let defaultValue = organizationCount >= 20 ? get(validOrganizationsOptions, '[1].value') : get(validOrganizationsOptions, '[0].value', 'all');

        const { signDocOptionsTmp } = getSignDocOptions({
            selectedOrgOptions: [defaultValue],
            adminOrganizations,
            categories: [SignDocCategories.FORCE_COMPLETION, SignDocCategories.CERTIFICATION],
            i18n
        });

        return {
            ...tableFilters,
            filterData: {
                ...tableFilters.filterData,
                organizations: validOrganizationsOptions,
                organizationsFull: adminOrganizations, // Pass full organization data for DataGridWrapper updates
                organizationIntersection: validOrganizationsOptions, // Same options for intersection filter
                signDocs: signDocOptionsTmp
            },
            filterValues: {
                ...tableFilters.filterValues,
                organizationIds: [defaultValue],
                organizationIntersectionIds: ['all'], // Default to ['all'] for no intersection
                signDocIds: ['all']
            }
        };
    };
    
    const [localTableFilters, setLocalTableFilters] = useState(() => initializeFilterModel());

    /**
     * Processes pie chart API result and updates state
     *
     * @param {object} pieChartResult - API response from getOrganizationProgressStatsAPI
     */
    const updatePieChartsFromApiResult = ({pieChartResult}) => {
        if (!pieChartResult || !pieChartResult.data) {
            setPieChartData([]);
            return;
        }

        // Process selected organizations data
        const selectedStatsData = {
            totalTasks: pieChartResult.data.totalTasks || 0,
            completedTasks: pieChartResult.data.completed || 0,
            inProgressTasks: pieChartResult.data.inProgress || 0,
            notStartedTasks: pieChartResult.data.notStarted || 0
        };
        const selectedChartData = preparePieChartData({ stats: selectedStatsData, i18n });

        setPieChartData(selectedChartData);
    };

    /**
     * Gets the columns configuration for the table
     * 
     * @param {object} params - Function parameters
     * @param {object} params.paginationModel - Pagination settings
     * @param {object} params.filterModel - Filter settings
     * @returns {Promise<Array>} Promise that resolves to array of column configurations
     */
    const getColumns = async () => {
        return getProgressReportColumns({ i18n, isCompanyAdmin: userContext?.isManagingCompany() });
    };

    /**
     * API call to get user progress report data
     * Fetches both table data and pie chart data in parallel to avoid double renders
     *
     * @param {object} params - Function parameters
     * @param {object} params.paginationModel - Pagination settings
     * @param {object} params.filterModel - Filter settings
     * @param {object} params.sortModel - Sort settings
     * @returns {Promise} API response with user progress data
     */
    const apiToGetData = async ({ paginationModel, filterModel, sortModel }) => {
        const organizationIds = get(filterModel, 'filterValues.organizationIds', []);

        if (organizationIds.length === 0) {
            // No organizations selected - clear pie chart
            setPieChartData([]);
            return { data: [], count: 0 };
        }

        // Fetch both table data and pie chart data in parallel
        const [tableResult, pieChartResult] = await Promise.all([
            getUserProgressReportAPI({
                paginationModel,
                filterModel,
                sortModel
            }),
            getOrganizationProgressStatsAPI({
                filterModel
            })
        ]);

        // Update pie chart state (single render with batched updates)
        updatePieChartsFromApiResult({pieChartResult});

        return tableResult;
    };

    return (
        <div className="UserProgressReportTable">
            <div className="UserProgressReportTable__charts-section">
                {pieChartData.length > 0 && (
                    <div className="UserProgressReportTable__chart-section">
                        <div className="UserProgressReportTable__info-bubble">
                            <LocalPopover 
                                info={i18n.translate('userProgressReport.pieChart.selectedOrgsInfoBubble')}
                            />
                        </div>
                        <h3 className="UserProgressReportTable__chart-title">
                            {i18n.translate('userProgressReport.pieChart.selectedOrgsTitle')}
                        </h3>
                        <div className="UserProgressReportTable__chart-container">
                            <PieChart
                                data={pieChartData}
                                questionText={String(i18n.translate('userProgressReport.pieChart.selectedOrgsSubtitle'))}
                                variant="doughnut"
                                showPercentages={true}
                                excludeZeroValues={true}
                                showLegend={true}
                                height={300}
                            />
                        </div>
                    </div>
                )}
            </div>
            
            <DataGridWrapper
                buttons={tableButtons}
                filterModel={localTableFilters}
                disableColumnFilter={true}
                paginationModel={paginationModel}
                sortModel={[]}
                checkboxSelection={false}
                onSelectionModelChange={null}
                selectionModel={[]}
                isRowSelectable={null}
                getColumns={getColumns}
                apiToGetData={apiToGetData}
                dataConverter={convertData}
                adminOrganizations={adminOrganizations}
            />
        </div>
    );
};

export default UserProgressReportTable; 